import { Link } from 'react-router-dom';

import type { ProceduralTimelineEvent } from '../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetProceduralTimelineEvents } from '@/api/queries/useGetProceduralTimelineEvents';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { cn } from '@/new/lib/utils';

// Window centred on the next upcoming event: up to 2 before, up to 4 after.
// Events with no start date sort to the end.
function getTimelineWindow(events: ProceduralTimelineEvent[]): ProceduralTimelineEvent[] {
  if (events.length === 0) return [];

  const sorted = events.slice().sort((a, b) => {
    const aTime = a.startDateTime !== null ? new Date(a.startDateTime).getTime() : Number.POSITIVE_INFINITY;
    const bTime = b.startDateTime !== null ? new Date(b.startDateTime).getTime() : Number.POSITIVE_INFINITY;
    return aTime - bTime;
  });

  const now = Date.now();
  const upcomingIndex = sorted.findIndex((e) => e.startDateTime !== null && new Date(e.startDateTime).getTime() >= now);
  // Nothing upcoming: anchor on the last event.
  const center = upcomingIndex === -1 ? sorted.length - 1 : upcomingIndex;

  const start = Math.max(0, center - 2);
  const end = Math.min(sorted.length, center + 5);
  return sorted.slice(start, end);
}

interface ProceduralTimelinePreviewProps {
  matterId: string;
}

export function ProceduralTimelinePreview({ matterId }: ProceduralTimelinePreviewProps) {
  const { data: timelineEvents } = useGetProceduralTimelineEvents(matterId);
  const timelineWindow = getTimelineWindow(timelineEvents ?? []);
  // First event in the window that hasn't happened yet, for styling its circle.
  const nextUpcomingIndex = timelineWindow.findIndex(
    (e) => e.startDateTime !== null && new Date(e.startDateTime).getTime() >= Date.now(),
  );

  const trackViewTimeline = (source: 'empty_state' | 'event') =>
    trackCustomEvent(TrackingEventsV2.ProceduralTimelineViewFromOverview, { matterId, source });

  return (
    <div className="my-4.5">
      <h2 className="mb-1 flex items-center gap-2 font-medium">
        Procedural timeline <NewChip className="relative top-auto right-auto" />
      </h2>
      <p className="mb-4 text-[13px] text-gray-500">Keep track of key dates for this Matter.</p>

      {timelineWindow.length === 0 ? (
        <div className="my-8 flex items-center gap-4">
          <div className="relative flex h-3 grow items-center mask-r-from-90% mask-l-from-90%">
            <div className="absolute inset-x-0 h-0.5 rounded-full bg-gray-100"></div>
            <div className="relative flex grow justify-evenly">
              <div className="size-3 rounded-full border-2 border-gray-200 bg-gray-50"></div>
              <div className="size-3 rounded-full border-2 border-gray-200 bg-gray-50"></div>
            </div>
          </div>
          <div className="space-y-1.5 text-center">
            <p>No timeline events yet</p>
            <Link
              to={`/matters/${matterId}/procedural-timeline`}
              onClick={() => trackViewTimeline('empty_state')}
              className="flex items-center gap-1 rounded-lg border bg-gray-50 px-2 py-1 text-xs text-gray-600 duration-150 hover:border-gray-300 hover:text-gray-700"
            >
              Upload a timetable to get started <Icon className="lucide--arrow-up-right" />
            </Link>
          </div>
          <div className="relative flex h-3 grow items-center mask-r-from-90% mask-l-from-90%">
            <div className="absolute inset-x-0 h-0.5 rounded-full bg-gray-100"></div>
            <div className="relative flex grow justify-evenly">
              <div className="size-3 rounded-full border-2 border-gray-200 bg-gray-50"></div>
              <div className="size-3 rounded-full border-2 border-gray-200 bg-gray-50"></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="my-4.5">
          {timelineWindow.map((event, index) => (
            <div key={event.id} className="group relative mb-3 flex w-full px-9">
              {/* Timeline circle and rail, matching the Kim chronology style */}
              <div
                className={cn(
                  'absolute top-1.5 left-0.75 z-10 size-3.25 rounded-full border-2',
                  event.startDateTime === null
                    ? 'border-gray-200 bg-gray-50' // unknown date
                    : nextUpcomingIndex === -1 || index < nextUpcomingIndex
                      ? 'border-teal-400 bg-teal-100' // completed (past)
                      : index === nextUpcomingIndex
                        ? 'border-teal-400 bg-white' // next event to happen
                        : 'border-gray-200 bg-gray-50', // future
                )}
              ></div>
              <div
                className={cn(
                  'absolute inset-0 -inset-y-1.5 w-px translate-x-[9px] bg-gray-200 group-first:top-1.5',
                  index === timelineWindow.length - 1 && 'bottom-[calc(100%-12px)]',
                )}
              />

              <Link
                to={`/matters/${matterId}/procedural-timeline`}
                onClick={() => trackViewTimeline('event')}
                className="mt-px block space-y-0.5"
              >
                <p>{event.name}</p>
                <div className="flex items-center gap-1.5 text-[13px]">
                  <p className="font-medium whitespace-nowrap text-gray-500">
                    {event.startDateTime ? formatDate(event.startDateTime) : 'TBD'}
                  </p>
                  <span className="font-extralight text-gray-400">•</span>
                  <p className="line-clamp-1 text-gray-500">{event.description}</p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
